import { type JSX, useEffect, useRef } from 'react'

import { Button, Icon, Input, cn } from '@hap/components'

interface InlineEditCellProps {
  /** Current display value */
  value: string

  /** Edit state: null = not editing, string = current edit value */
  editValue: string | null

  /** Called when edit value changes */
  onEditValueChange: (value: string | null) => void

  /** Called when user submits (Enter key or check button) */
  onSubmit: (value: string) => void

  /** Called when canceling (especially for new row creation) */
  onCancel?: () => void

  /** Placeholder shown in input when editing */
  placeholder?: string

  /** ID for data-edit-target attribute (enables dropdown menu "Edit" action) */
  editTargetId?: string

  /** Whether the submit button should be disabled */
  submitDisabled?: boolean

  /** Additional class name for the wrapper */
  className?: string
}

/**
 * A generic inline edit component for table cells.
 *
 * Features:
 * - Toggles between display mode and edit mode
 * - Shows pencil icon on hover in display mode
 * - Shows check/x buttons in edit mode
 * - Handles Enter to submit, Escape to cancel
 * - Auto-focuses and selects text when entering edit mode
 * - Supports data-edit-target for dropdown menu integration
 *
 * @example
 * const [editValue, setEditValue] = useState<string | null>(null)
 *
 * <InlineEditCell
 *   value={getValue()}
 *   editValue={editValue}
 *   onEditValueChange={setEditValue}
 *   editTargetId={`${row.original.id}-name`}
 *   placeholder="Enter name"
 *   onSubmit={(value) => {
 *     if (row.original.id) {
 *       update({ id: row.original.id, values: { name: value } })
 *     } else {
 *       create({ name: value })
 *     }
 *   }}
 *   onCancel={!row.original.id ? row.original.onSubmit : undefined}
 * />
 */
export function InlineEditCell({
  value,
  editValue,
  onEditValueChange,
  onSubmit,
  onCancel,
  placeholder,
  editTargetId,
  submitDisabled,
  className,
}: InlineEditCellProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const isEditing = editValue !== null

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSubmit = () => {
    if (editValue === null) return
    const trimmed = editValue.trim()
    if (!trimmed) return

    onEditValueChange(null)
    onSubmit(trimmed)
  }

  const handleCancel = () => {
    onEditValueChange(null)
    onCancel?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && editValue !== value) {
      handleSubmit()
    }
    if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const enterEditMode = () => {
    onEditValueChange(value)
  }

  const isSubmitDisabled = submitDisabled ?? !editValue?.trim()

  return (
    <div className={cn('-ml-2', className)}>
      <div className="group inline-flex items-center gap-3 whitespace-nowrap rounded-md">
        {isEditing ? (
          <>
            <Input
              ref={inputRef}
              value={editValue}
              className="h-auto px-2 py-1 transition-all"
              onChange={(e) => onEditValueChange(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
            />
            <Button
              icon="check"
              size="icon-sm"
              className="shrink-0"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            />
            <Button
              icon="arrow-u-up-left"
              variant="secondary"
              size="icon-sm"
              className="shrink-0"
              onClick={handleCancel}
            />
          </>
        ) : (
          <div
            data-edit-target={editTargetId}
            onClick={enterEditMode}
            className="flex cursor-pointer items-center gap-1"
          >
            <div className="group-hover:bg-input-background rounded-md border border-transparent px-2 py-1">
              {value}
            </div>
            <Icon
              name="pencil-simple"
              className="size-4 opacity-0 group-hover:opacity-100"
            />
          </div>
        )}
      </div>
    </div>
  )
}
